import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { getVersion } from '@fc-shared/utils/data.utils';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { HasFeatureModule } from '@fc-shared/directives/has-feature/has-feature.module';
import { AvatarModule } from '@fc-shared/ui/avatar/avatar.module';
import {
  currentUserHasLegacyPermission,
  getCurrentEmployee,
  getCurrentUser,
  getEmployeeById,
} from '@fc-store/selectors';
import { AuthService, DialogsService } from '@fc-core';
import { PermissionsService } from '@fc-core/services/permissions.service';
import { PwaService } from '@fc-core/services/pwa-service';
import { CopyToClipDirective } from '@fc-shared/directives/copy-to-clip.directive';
import { AmplitudeService } from '@fc-core/services/amplitude.service';
import { MenuComponent } from '@fc-shared/ui/menus/menu.component';
import { MenuItemComponent } from '@fc-shared/ui/menus/menu-item.component';
import { MenuDividerComponent } from '@fc-shared/ui/menus/menu-divider.component';
import { OverlayClickDirective } from '@fc-shared/ui/fc-overlay/overlay-click.directive';
import { BadgeComponent } from '@fc-shared/ui/badge/badge.component';
import { HeaderFacade } from './header.facade';
import { HelpCenterDialogComponent } from '@fc-core/components/header/help-center-dialog/help-center-dialog.component';
import { getShowNews } from '@fc-store/selectors/news.selectors';

@Component({
  selector: 'fc-employee-menu',
  imports: [
    RouterModule,
    ButtonComponent,
    IconComponent,
    HasFeatureModule,
    AvatarModule,
    CopyToClipDirective,
    MenuComponent,
    MenuItemComponent,
    MenuDividerComponent,
    OverlayClickDirective,
    BadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <fc-menu class="flex" position="bottom-right">
      <div
        #employeeMenuTrigger="fcOverlayClick"
        fcOverlayClick
        role="button"
        tabindex="0"
        aria-label="Account menu"
        class="flex cursor-pointer"
        [class.is-hijacked]="isHijacked"
        (click)="triggerAnalytics('topbar_click_user_dropdown')"
        (keydown.enter)="clickMenuTriggerFromKeyboard($event)"
        (keydown.space)="clickMenuTriggerFromKeyboard($event)"
      >
        <fc-entity-avatar
          type="employee"
          [size]="36"
          [entity]="currentEmployee()"
        ></fc-entity-avatar>

        @if (isHijacked && currentUser()) {
          <div class="-ml-[10px] border-2 border-sky-200 rounded-full">
            <fc-avatar-placeholder
              [color]="'#FFC107'"
              [fontSize]="12"
              [size]="36"
              [text]="currentUser().email[0]"
              shape="circle"
            ></fc-avatar-placeholder>
          </div>
        }
      </div>

      <div class="flex min-w-200 max-w-280 flex-col">
        @if (isHijacked && currentUser()) {
          <span
            class="w-full flex items-center gap-12 py-12 px-16 text-grey-300 text-sm"
          >
            {{ currentUser().email }} user
          </span>
        }
        <fc-menu-item
          [routerLink]="['/settings/account']"
          tabindex="-1"
          (click)="triggerAnalytics('user_dropdown_click_account')"
        >
          <fc-entity-avatar
            type="employee"
            [size]="20"
            [entity]="currentEmployee()"
          />
          My Account
        </fc-menu-item>
        <div class="flex flex-row px-8 py-4 w-full">
          <fc-button
            class="w-full"
            variant="outline"
            size="sm"
            (click)="employeeMenuTrigger.close(); openStatusSetDialog()"
            color="secondary"
            icon="icon-emoji"
          >
            {{ statusText }}
          </fc-button>
        </div>

        <fc-menu-item class="sm:hidden" (click)="openActivity()">
          <fc-icon [size]="20" icon="icon-notification-outline" />
          Activity
        </fc-menu-item>

        <fc-menu-item class="sm:hidden" (click)="openMobileHelp()">
          <fc-icon [size]="20" icon="icon-help-outline" />
          Help center
        </fc-menu-item>

        <fc-menu-item class="sm:hidden" (click)="openNews()">
          <fc-icon [size]="20" icon="icon-news-outline" />
          <span>News</span>
          @if (unreadNewsCount() > 0) {
            <fc-badge class="ml-auto" [count]="unreadNewsCount()" />
          }
        </fc-menu-item>

        @if (permissions.havePermissions) {
          <fc-menu-item
            [disabled]="!pwaService.canInstall()"
            (click)="pwaService.initAppToScreen()"
          >
            <fc-icon [size]="20" icon="icon-download" />
            Add To Home Screen
          </fc-menu-item>
        }
        @if (haveDevOptions()) {
          <fc-menu-item (click)="openDeveloperOptions()">
            <fc-icon [size]="20" icon="icon-scroll" />
            Developer Options
          </fc-menu-item>
        }
        @if (canSwitchCustomer()) {
          <fc-menu-item (click)="openChangeCustomer()">
            <fc-icon [size]="20" icon="icon-business" />
            Change Customer
          </fc-menu-item>
        }

        <fc-menu-divider />
        <fc-menu-item fcCopyToClip [text]="version ?? ''">
          <fc-icon [size]="20" icon="icon-copy" />
          <div class="flex flex-1 flex-row justify-between">
            <div class="text-sm text-grey-900">Version:</div>
            <div class="text-xs text-secondary-400">{{ version }}</div>
          </div>
        </fc-menu-item>
        <fc-menu-divider />
        <fc-menu-item (click)="openConfirmLogout()">
          <fc-icon [size]="20" icon="icon-logout" />
          Logout
        </fc-menu-item>
      </div>
    </fc-menu>
  `,
})
export class EmployeeMenuComponent {
  store = inject(Store);
  amplitude = inject(AmplitudeService);
  router = inject(Router);
  dialogs = inject(DialogsService);
  permissions = inject(PermissionsService);
  pwaService = inject(PwaService);
  authService = inject(AuthService);
  headerFacade = inject(HeaderFacade);

  isHijacked = this.authService.tokenData?.is_hijacked;
  currentEmployee = computed(() => {
    const currentEmployee = this.store.selectSignal(getCurrentEmployee)();
    if (!currentEmployee?.id) return undefined;
    return this.store.selectSignal(getEmployeeById(currentEmployee.id))();
  });
  currentUser = this.store.selectSignal(getCurrentUser);
  unreadNewsCount = this.store.selectSignal(getShowNews);
  canSwitchCustomer = this.store.selectSignal(
    currentUserHasLegacyPermission(
      'accounting_customer_can_switch_customer_view',
    ),
  );
  haveDevOptions = computed(() => this.currentUser()?.isStaff);

  openNews() {
    this.amplitude.track('topbar_click_news');
    this.headerFacade.showNews.set(true);
    this.headerFacade.showEvents.set(false);
  }

  openActivity() {
    this.amplitude.track('topbar_click_activity');
    this.headerFacade.showNews.set(false);
    this.headerFacade.showEvents.set(true);
  }

  get version(): string | undefined {
    return getVersion();
  }

  get statusText(): string {
    return this.headerFacade.getStatusText(this.currentEmployee());
  }

  triggerAnalytics(event: string): void {
    this.amplitude.track(event);
  }

  protected clickMenuTriggerFromKeyboard(event: Event): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).click();
  }

  openDeveloperOptions() {
    this.headerFacade.openDeveloperOptions();
  }

  openConfirmLogout() {
    this.headerFacade.openConfirmLogout();
  }

  openMobileHelp() {
    this.amplitude.track('topbar_click_help');
    this.dialogs.openMobileDialog(HelpCenterDialogComponent);
  }

  openChangeCustomer() {
    if (this.canSwitchCustomer()) {
      this.headerFacade.openChangeCustomer();
    }
  }

  openStatusSetDialog(): void {
    this.headerFacade.openStatusSetDialog(
      this.currentEmployee()?.id,
      this.currentEmployee()?.setstatus,
    );
  }
}
